import { Component } from "react";
import { injectIntl, defineMessages } from "react-intl";
import classNames from "classnames";

import Modal from "../Modals/Modal";
import withAlert from "../Alerts/withAlert";
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from "../Alerts/Alert";
import Icon from "../Utils/Icon";
import AuthenticationTwoFactorManager from "./AuthenticationTwoFactorManager";
import { withConfirmationForm } from "./withConfirmationForm";
import getQueryStringParamByName from "../../helpers/getQueryStringParamByName";

import { confirmAccount } from "../../mutations/ConfirmAccountMutation";

class AccountConfirmation extends Component {
  constructor(props) {
    super(props);
    this.onSendResponse = this.onSendResponse.bind(this);
    this.handleSignedIn = this.handleSignedIn.bind(this);

    this.state = {
      status: "submitting",
      twoFactorStep: null
    };
  }

  get labels() {
    const { formatMessage } = this.props.intl;

    return {
      title: formatMessage(intlMessages.title),
      submitting: formatMessage(intlMessages.submitting),
      returnedSuccess: formatMessage(intlMessages.returnedSuccess),
      returnedError: formatMessage(intlMessages.returnedError, {
        a: message => `<a href="#resend-confirmation-instructions">${message}</a>`
      }),
      successMessage: formatMessage(intlMessages.successMessage)
    };
  }

  get isSubmitting() {
    return this.state.status === "submitting";
  }

  get modalClassName() {
    return classNames({
      "modal-sm": true,
      "modal-loader": this.isSubmitting
    });
  }

  get message() {
    if (this.state.alertMessage) {
      return this.state.alertMessage.content;
    } else {
      return this.labels[this.state.status];
    }
  }

  get loadingSpinner() {
    if (!this.isSubmitting) return null;

    return (
      <div className="loading-spinner-container modal-loading-spinner">
        <Icon className="fa fa-circle-notch fa-spin" />
      </div>
    );
  }

  componentDidMount() {
    confirmAccount(
      { confirmationToken: getQueryStringParamByName("confirmation_token") },
      this.onSendResponse
    );
  }

  handleSignedIn() {
    window.location.href = "/";
  }

  onSendResponse(response) {
    if (response.confirmAccount.twoFactorStep) {
      // The account is confirmed but the platform requires SMS two-factor before sign-in:
      // hand off to the same challenge/enrollment flow used on the sign-in screen.
      this.setState({ twoFactorStep: response.confirmAccount.twoFactorStep });
    } else if (response.confirmAccount.success) {
      this.setState({ status: "returnedSuccess" });

      this.props.showAlert({
        message: this.labels.successMessage,
        type: ALERT_TYPE_SUCCESS
      });

      // Redirect to home page and reload since the user has been automatically
      // signed in on account confirmation.
      setTimeout(() => (window.location.href = "/"), 3000);
    } else {
      this.setState({ status: "returnedError" });

      this.props.showAlert({
        message: response.confirmAccount.notice,
        type: ALERT_TYPE_ERROR,
        onClick: this.props.onAlertMessageClicked
      });
    }
  }

  render() {
    return (
      <Modal
        isOpen={true}
        shouldCloseOnOverlayClick={false}
        breadcrumbs={this.breadcrumbs}
        title={this.labels.title}
        className={this.modalClassName}
      >
        {this.state.twoFactorStep ? (
          <AuthenticationTwoFactorManager
            twoFactorStep={this.state.twoFactorStep}
            onSignIn={this.handleSignedIn}
          />
        ) : (
          <div className="box margin-bottom-xs padding-x-lg padding-y-md">
            <p
              className="help-block"
              dangerouslySetInnerHTML={{ __html: this.message }}
              onClick={this.props.onAlertMessageClicked}
            />

            {this.loadingSpinner}
          </div>
        )}
      </Modal>
    );
  }
}

export default injectIntl(withConfirmationForm(withAlert(AccountConfirmation)));

const intlMessages = defineMessages({
  title: {
    id: "authentication.accountConfirmation.title",
    defaultMessage: "Confirmation de votre compte"
  },
  submitting: {
    id: "authentication.accountConfirmation.submitting",
    defaultMessage: `
      Votre compte va maintenant être confirmé à partir du code contenu
      dans le lien que vous venez d'utiliser. Merci de patienter ...
    `
  },
  returnedSuccess: {
    id: "authentication.accountConfirmation.returnedSuccess",
    defaultMessage: "Votre compte a bien été confirmé."
  },
  returnedError: {
    id: "authentication.accountConfirmation.returnedError",
    defaultMessage: `
    Votre compte n'a pu être confirmé.
    Vous pouvez demander un nouveau lien de confirmation en cliquant ici :
    <a>Demander un nouveau mail de confirmation</a>
  `
  },
  successMessage: {
    id: "authentication.accountConfirmation.successMessage",
    defaultMessage: `
      Votre adresse e-mail a bien été confirmée, et vous avez été
      automatiquement connecté à votre compte. Vous allez être redirigé
      vers la page d'accueil dans quelques instants ...
    `
  }
});
